import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  List,
  Paper,
  Stack,
  Stepper,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCircleCheck,
} from "@tabler/icons-react";
import { useClient } from "../../../context/ClientContext";
import { useSalon } from "../../../context/SalonContext";
import { useLoyaltyProgram } from "../../../hooks/useLoyaltyProgram";
import { SalonContextType } from "../../../types/context";
import { Client, MergeClientsResponse } from "../../../types/client";
import MergeSelectClientsStep from "./MergeSelectClientsStep";
import MergeFieldsStep from "./MergeFieldsStep";
import MergeConfirmStep from "./MergeConfirmStep";
import { buildFieldValues } from "./mergeFields";
import { summarizeMergeCounts } from "./mergeSummary";
import styles from "./ClientMergeWizard.module.css";

const GIFT_CARDS_ENABLED = process.env.REACT_APP_GIFT_CARDS_ENABLED === "true";

const CLIENTS_PATH = "/admin/clients";

interface MergeLocationState {
  clientIds?: string[];
}

/**
 * Three-step client merge flow: Select Clients -> Choose Fields -> Confirm.
 * Reached from the clients list floating bar or the duplicates review page,
 * with the candidate client uuids passed via router state.
 */
const ClientMergeWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { allClients, mergeClients } = useClient();
  const { businessInfo } = useSalon() as SalonContextType;
  const {
    isEnabled: loyaltyEnabled,
    apiBusinessLocationId: loyaltyLocationId,
  } = useLoyaltyProgram();
  // Loyalty Points only make sense to preview when the salon's program is on.
  const loyaltyActive = loyaltyEnabled && !!loyaltyLocationId;

  const initialIds = useMemo(
    () => (location.state as MergeLocationState)?.clientIds ?? [],
    [location.state],
  );

  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialIds.slice(0, 3),
  );
  const [winningClientId, setWinningClientId] = useState<string | null>(
    initialIds[0] ?? null,
  );
  const [active, setActive] = useState(0);
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, string>
  >({});
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [merging, setMerging] = useState(false);
  const [done, setDone] = useState(false);
  const [mergedClientId, setMergedClientId] = useState<string | null>(null);
  const [mergeCounts, setMergeCounts] = useState<Record<string, number> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const records = useMemo<Client[]>(
    () =>
      selectedIds
        .map((id) => allClients.find((c: Client) => c.id === id))
        .filter((c): c is Client => Boolean(c)),
    [selectedIds, allClients],
  );

  const winningRecord =
    records.find((r) => r.id === winningClientId) ?? records[0] ?? null;

  const fieldValues = useMemo(
    () =>
      winningRecord
        ? buildFieldValues(records, winningRecord.id, fieldSelections)
        : {},
    [records, winningRecord, fieldSelections],
  );

  const goToClients = () => navigate(CLIENTS_PATH);

  const handleRemove = (id: string) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    if (winningClientId === id) {
      setWinningClientId(selectedIds.find((x) => x !== id) ?? null);
    }
    // Drop any per-field selections that pointed at the removed record. Without
    // this, the Choose Fields chooser would show no highlighted radio for those
    // rows (the stale id matches no rendered record); buildFieldValues already
    // falls back to the winner, so this only keeps the UI in sync.
    setFieldSelections((prev) => {
      const next: Record<string, string> = {};
      for (const fieldKey of Object.keys(prev)) {
        if (prev[fieldKey] !== id) next[fieldKey] = prev[fieldKey];
      }
      return next;
    });
  };

  const handleFieldChange = (fieldKey: string, recordId: string) => {
    setFieldSelections((prev) => ({ ...prev, [fieldKey]: recordId }));
  };

  const handleMerge = async () => {
    if (!winningRecord) return;
    const winnerId = winningRecord.id;
    const losingIds = selectedIds.filter((id) => id !== winnerId);
    try {
      setMerging(true);
      setError(null);
      const result = (await mergeClients(winnerId, losingIds, fieldValues)) as
        | MergeClientsResponse
        | undefined;
      // Surface what actually moved (packages, gift cards, loyalty points, …)
      // on the success screen instead of discarding the response.
      setMergeCounts(result?.merge_summary?.repointed_counts ?? null);
      // Capture the winner before the refreshed client list drops the losers
      // (which would otherwise trip the "select two clients" guard below).
      setMergedClientId(winnerId);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to merge clients.");
    } finally {
      setMerging(false);
    }
  };

  // Success screen — checked BEFORE the guard, because a completed merge drops
  // the losing records from the client list, shrinking `records` below 2.
  if (done) {
    const summaryLines = summarizeMergeCounts(mergeCounts);
    return (
      <Center mih="60dvh" p="lg">
        <Stack align="center" gap="md" maw={460}>
          <ThemeIcon
            radius="xl"
            size={84}
            variant="light"
            color="var(--brand-teal)"
          >
            <IconCircleCheck size={48} />
          </ThemeIcon>
          <Title order={2} ta="center">
            Clients Merged Successfully
          </Title>
          <Text ta="center" c="dimmed">
            The selected client records have been permanently combined into a
            single profile. All historical data and appointments were preserved.
          </Text>
          {summaryLines.length > 0 && (
            <Paper withBorder radius="md" p="md" w="100%">
              <Text size="xs" c="dimmed" mb="xs">
                MOVED TO THE SURVIVING PROFILE
              </Text>
              <List
                spacing="xs"
                size="sm"
                center
                icon={<IconCircleCheck size={16} color="var(--brand-teal)" />}
              >
                {summaryLines.map((line) => (
                  <List.Item key={line}>{line}</List.Item>
                ))}
              </List>
            </Paper>
          )}
          <Group justify="center" gap="md" mt="xs">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={goToClients}
            >
              Back to Clients
            </Button>
            {mergedClientId && (
              <Button
                color="var(--brand-teal)"
                onClick={() => navigate(`${CLIENTS_PATH}/${mergedClientId}`)}
              >
                Go to Merged Client
              </Button>
            )}
          </Group>
        </Stack>
      </Center>
    );
  }

  // Guard: state lost (e.g. page refresh) or records not loaded.
  if (!winningRecord || records.length < 2) {
    return (
      <Box p="lg">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={goToClients}
          mb="md"
        >
          Back to Clients
        </Button>
        <Alert color="yellow" icon={<IconAlertCircle size={18} />}>
          Select at least two clients from the list to start a merge.
        </Alert>
      </Box>
    );
  }

  const isLastStep = active === 2;

  return (
    <Box className={styles.page}>
      <Box className={styles.content}>
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={goToClients}
          mb="md"
        >
          Back to Clients
        </Button>

        <Stepper active={active} color="var(--brand-teal)" mb="xl">
          <Stepper.Step label="Select Clients" />
          <Stepper.Step label="Choose Fields" />
          <Stepper.Step label="Confirm" />
        </Stepper>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={18} />} mb="md">
            {error}
          </Alert>
        )}

        {active === 0 && (
          <MergeSelectClientsStep
            records={records}
            winningClientId={winningClientId}
            onSetWinner={setWinningClientId}
            onRemove={handleRemove}
          />
        )}
        {active === 1 && (
          <MergeFieldsStep
            records={records}
            winningClientId={winningRecord.id}
            selections={fieldSelections}
            onChange={handleFieldChange}
          />
        )}
        {active === 2 && (
          <MergeConfirmStep
            records={records}
            winningRecord={winningRecord}
            fieldValues={fieldValues}
            vertical={businessInfo?.business_vertical}
            giftCardsEnabled={GIFT_CARDS_ENABLED}
            loyaltyActive={loyaltyActive}
          />
        )}
      </Box>

      <Box className={styles.footer}>
        <Box className={styles.footerInner}>
          {isLastStep && (
            <Checkbox
              mb="md"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.currentTarget.checked)}
              color="var(--brand-teal)"
              label={
                <Text fw={600}>
                  I understand that merging these clients cannot be undone
                </Text>
              }
              description={`I understand that this action will permanently merge these ${records.length} records into one and there is no way to undo it. All of the above data will be nested under the new target client and previous client details (such as their phone number and email address) will be unrecoverable.`}
            />
          )}
          <Group justify="space-between" wrap="nowrap" align="center">
            <Button
              variant="default"
              onClick={() =>
                active === 0 ? goToClients() : setActive(active - 1)
              }
            >
              {active === 0 ? "Cancel" : "Back"}
            </Button>
            {isLastStep ? (
              <Button
                color="var(--brand-teal)"
                disabled={!confirmChecked}
                loading={merging}
                onClick={handleMerge}
              >
                Merge Clients
              </Button>
            ) : (
              <Button
                color="var(--brand-teal)"
                onClick={() => setActive(active + 1)}
              >
                Next
              </Button>
            )}
          </Group>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientMergeWizard;
